import React, { useState, useEffect, useMemo } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import AddBook from './AddBook'
import MarkAsRead from './MarkAsRead'
import StartReading from './StartReading'
import BookSearchPreview from './BookSearchPreview'
import { formatDate } from '../utils/dateFormat'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  asin?: string
  bookshop_slug?: string
  description?: string
  cover_url?: string
  genre?: string
  first_publish_year?: number
  number_of_pages?: number
}

interface ToReadBook {
  id: number
  notes?: string
  added_at: string
  book: Book
}

interface MyToReadListProps {
  onBack: () => void
}

type SortField = 'custom' | 'title' | 'author' | 'genre' | 'added_at' | 'year'
type SortDirection = 'asc' | 'desc'

// Sortable book item component
function SortableBookItem({
  trb,
  onPreview,
  onStartReading,
  onMarkAsRead,
  onDelete,
  deletingId,
  openMenuId,
  setOpenMenuId,
}: {
  trb: ToReadBook
  onPreview: (book: Book) => void
  onStartReading: (trb: ToReadBook) => void
  onMarkAsRead: (trb: ToReadBook) => void
  onDelete: (id: number) => void
  deletingId: number | null
  openMenuId: number | null
  setOpenMenuId: (id: number | null) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: trb.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex gap-4 p-4 hover:bg-gray-50 transition-colors">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
          title="Drag to reorder"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </button>

        {/* Clickable Book Info */}
        <button
          onClick={() => onPreview(trb.book)}
          className="flex gap-4 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
        >
          {trb.book.cover_url ? (
            <img
              src={trb.book.cover_url}
              alt={trb.book.title}
              className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0"
            />
          ) : (
            <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 mb-1 truncate">{trb.book.title}</h3>
            <p className="text-sm text-gray-600 mb-2">by {trb.book.author}</p>
            {trb.book.genre && (
              <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium mb-2">
                {trb.book.genre}
              </span>
            )}
            {trb.notes && (
              <p className="text-sm text-gray-500 italic mb-2">"{trb.notes}"</p>
            )}
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>Added {formatDate(trb.added_at)}</span>
              {trb.book.first_publish_year && (
                <>
                  <span>•</span>
                  <span>{trb.book.first_publish_year}</span>
                </>
              )}
              {trb.book.number_of_pages && (
                <>
                  <span>•</span>
                  <span>{trb.book.number_of_pages} pages</span>
                </>
              )}
            </div>
          </div>
        </button>

        {/* Actions Menu */}
        <div className="relative flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setOpenMenuId(openMenuId === trb.id ? null : trb.id)
            }}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actions"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
          {openMenuId === trb.id && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setOpenMenuId(null)}
              />
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                <button
                  onClick={() => {
                    onStartReading(trb)
                    setOpenMenuId(null)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Start Reading
                </button>
                <button
                  onClick={() => {
                    onMarkAsRead(trb)
                    setOpenMenuId(null)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Mark as Read
                </button>
                <button
                  onClick={() => {
                    onDelete(trb.id)
                    setOpenMenuId(null)
                  }}
                  disabled={deletingId === trb.id}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {deletingId === trb.id ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function MyToReadList({ onBack }: MyToReadListProps) {
  const [user, setUser] = useState<{ preferred_affiliate_provider?: string | null } | null>(null)
  const [toReadBooks, setToReadBooks] = useState<ToReadBook[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddBook, setShowAddBook] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [showMarkAsRead, setShowMarkAsRead] = useState(false)
  const [showStartReading, setShowStartReading] = useState(false)
  const [selectedBook, setSelectedBook] = useState<ToReadBook | null>(null)
  const [previewBook, setPreviewBook] = useState<Book | null>(null)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [genreFilter, setGenreFilter] = useState<string>('all')
  const [sortField, setSortField] = useState<SortField>('custom')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [userResponse, booksResponse] = await Promise.all([
        fetch('/api/v1/me'),
        fetch('/api/v1/to_read_books')
      ])

      if (userResponse.ok) {
        const userData = await userResponse.json()
        setUser(userData.user)
      }

      if (booksResponse.ok) {
        const booksData = await booksResponse.json()
        setToReadBooks(booksData.to_read_books)
      }
    } catch (err) {
      console.error('Failed to fetch data:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchToReadBooks = async () => {
    try {
      const response = await fetch('/api/v1/to_read_books')
      if (response.ok) {
        const data = await response.json()
        setToReadBooks(data.to_read_books)
      }
    } catch (err) {
      console.error('Failed to fetch to-read books:', err)
    }
  }

  const handleProviderSelected = async (provider: string) => {
    if (user) {
      // Update local state immediately for responsiveness
      setUser({ ...user, preferred_affiliate_provider: provider })

      // Save to backend
      try {
        await fetchWithCsrf('/api/v1/profile', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user: {
              preferred_affiliate_provider: provider
            }
          })
        })
      } catch (err) {
        console.error('Failed to save affiliate provider preference:', err)
      }
    }
  }

  const handleDelete = async (id: number) => {
    setDeletingId(id)
    try {
      const response = await fetchWithCsrf(`/api/v1/to_read_books/${id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setToReadBooks(toReadBooks.filter(trb => trb.id !== id))
      }
    } catch (err) {
      console.error('Failed to delete book:', err)
    } finally {
      setDeletingId(null)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = toReadBooks.findIndex(trb => trb.id === active.id)
    const newIndex = toReadBooks.findIndex(trb => trb.id === over.id)

    const newOrder = arrayMove(toReadBooks, oldIndex, newIndex)
    setToReadBooks(newOrder)

    // Switch to custom sort to preserve manual order
    setSortField('custom')

    try {
      await fetchWithCsrf('/api/v1/to_read_books/reorder', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          book_ids: newOrder.map(trb => trb.id),
        }),
      })
    } catch (err) {
      console.error('Failed to save book order:', err)
      fetchToReadBooks()
    }
  }

  // Get unique genres for filter
  const genres = useMemo(() => {
    const uniqueGenres = new Set(toReadBooks.map(trb => trb.book.genre).filter(Boolean))
    return Array.from(uniqueGenres).sort()
  }, [toReadBooks])

  // Filter and sort books
  const filteredAndSortedBooks = useMemo(() => {
    let filtered = toReadBooks

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(trb =>
        trb.book.title.toLowerCase().includes(query) ||
        trb.book.author.toLowerCase().includes(query)
      )
    }

    // Genre filter
    if (genreFilter !== 'all') {
      filtered = filtered.filter(trb => trb.book.genre === genreFilter)
    }

    // Sort (skip for custom/manual order)
    if (sortField === 'custom') {
      return filtered
    }

    const sorted = [...filtered].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (sortField) {
        case 'title':
          aVal = a.book.title.toLowerCase()
          bVal = b.book.title.toLowerCase()
          break
        case 'author':
          aVal = a.book.author.toLowerCase()
          bVal = b.book.author.toLowerCase()
          break
        case 'genre':
          aVal = a.book.genre?.toLowerCase() || ''
          bVal = b.book.genre?.toLowerCase() || ''
          break
        case 'year':
          aVal = a.book.first_publish_year || 0
          bVal = b.book.first_publish_year || 0
          break
        case 'added_at':
          aVal = new Date(a.added_at).getTime()
          bVal = new Date(b.added_at).getTime()
          break
        default:
          return 0
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [toReadBooks, searchQuery, genreFilter, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      )
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    )
  }

  const activeFiltersCount = [
    searchQuery !== '',
    genreFilter !== 'all'
  ].filter(Boolean).length

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">Want to Read</h1>
              <p className="text-gray-600 mt-1">Your reading wishlist ({toReadBooks.length} books)</p>
            </div>
            <button
              onClick={() => setShowAddBook(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Book
            </button>
          </div>

          {/* Filters and Search */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by title or author..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              />
            </div>
            <select
              value={genreFilter}
              onChange={(e) => setGenreFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            >
              <option value="all">All Genres</option>
              {genres.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
            <select
              value={sortField}
              onChange={(e) => handleSort(e.target.value as SortField)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            >
              <option value="custom">Sort by: Custom Order</option>
              <option value="added_at">Sort by: Added Date</option>
              <option value="title">Sort by: Title</option>
              <option value="author">Sort by: Author</option>
              <option value="genre">Sort by: Genre</option>
              <option value="year">Sort by: Year</option>
            </select>
          </div>

          {/* Active Filters */}
          {activeFiltersCount > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <span className="text-sm text-gray-600">Active filters:</span>
              {searchQuery && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs">
                  Search: "{searchQuery}"
                </span>
              )}
              {genreFilter !== 'all' && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs">
                  Genre: {genreFilter}
                </span>
              )}
              <button
                onClick={() => {
                  setSearchQuery('')
                  setGenreFilter('all')
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium ml-2"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Books List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {filteredAndSortedBooks.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredAndSortedBooks.map(trb => trb.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 divide-y divide-gray-200">
                {filteredAndSortedBooks.map(trb => (
                  <SortableBookItem
                    key={trb.id}
                    trb={trb}
                    onPreview={setPreviewBook}
                    onStartReading={(trb) => {
                      setSelectedBook(trb)
                      setShowStartReading(true)
                    }}
                    onMarkAsRead={(trb) => {
                      setSelectedBook(trb)
                      setShowMarkAsRead(true)
                    }}
                    onDelete={handleDelete}
                    deletingId={deletingId}
                    openMenuId={openMenuId}
                    setOpenMenuId={setOpenMenuId}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-600">
              {activeFiltersCount > 0 ? 'No books match your filters' : 'No books in your reading list yet'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {activeFiltersCount > 0 ? 'Try adjusting your search or filters' : 'Start adding books to build your wishlist!'}
            </p>
          </div>
        )}

        {/* Results counter */}
        {filteredAndSortedBooks.length > 0 && (
          <div className="mt-4 text-sm text-gray-600 text-center">
            Showing {filteredAndSortedBooks.length} of {toReadBooks.length} books
          </div>
        )}
      </div>

      {/* Add Book Modal */}
      {showAddBook && (
        <AddBook
          onClose={() => setShowAddBook(false)}
          onSuccess={() => {
            setShowAddBook(false)
            fetchToReadBooks()
          }}
          defaultStatus="want_to_read"
          userProvider={user?.preferred_affiliate_provider}
          onProviderSelected={handleProviderSelected}
        />
      )}

      {/* Start Reading Modal */}
      {showStartReading && selectedBook && (
        <StartReading
          book={selectedBook.book}
          toReadBookId={selectedBook.id}
          onClose={() => {
            setShowStartReading(false)
            setSelectedBook(null)
          }}
          onSuccess={() => {
            setShowStartReading(false)
            setSelectedBook(null)
            fetchToReadBooks()
          }}
        />
      )}

      {/* Mark as Read Modal */}
      {showMarkAsRead && selectedBook && (
        <MarkAsRead
          book={selectedBook.book}
          toReadBookId={selectedBook.id}
          onClose={() => {
            setShowMarkAsRead(false)
            setSelectedBook(null)
          }}
          onSuccess={() => {
            setShowMarkAsRead(false)
            setSelectedBook(null)
            fetchToReadBooks()
          }}
        />
      )}

      {/* Book Preview Modal */}
      {previewBook && (
        <BookSearchPreview
          book={previewBook}
          onClose={() => setPreviewBook(null)}
          onSelect={() => setPreviewBook(null)}
          userProvider={user?.preferred_affiliate_provider}
          onProviderSelected={handleProviderSelected}
        />
      )}
    </div>
  )
}
